import { createContext, IContextComponents } from '../../utilities/components/Context';
import { EventGroup } from '@uifabric/utilities/lib/EventGroup';
import { IKeyboardShortcutProps } from './KeyboardShortcut';
import { autobind } from 'office-ui-fabric-react/lib/Utilities';
import { getKeyPressed, isInputElement, isButtonElement } from './keyboardMapping';

export interface IKeyboardContext {
  keyboardManager: KeyboardManager;
}

export interface IKeyboardManagerProps {
  isDefault: boolean;
}

export class KeyboardManager {
  private _isDefault: boolean;
  private _isActive: boolean;
  private _hasActiveChildren: number;
  private _events: EventGroup;
  private _raisedEvent: boolean;

  private _shortcuts: {
    [key: string]: {
      keySequences: string[];
      description: string;
      onExecute: (event?: React.KeyboardEvent<HTMLElement> | undefined) => void;
    }
  } = {};

  constructor(props: IKeyboardManagerProps) {
    this._events = new EventGroup(this);
    this._isDefault = props.isDefault;
    this._isActive = false;
    this._hasActiveChildren = 0;
    this._shortcuts = {};
    this._raisedEvent = false;

    if (this._events) {
      this._events.on(document, 'keydown', this._onProcessKeys);
      this._events.on(document, 'resetKeyboardZoneState', (evt: { args: KeyboardManager }) => {
        if (!this._raisedEvent) {
          this._isActive = false;
        } else {
          // clear out the current event init flag ready for next event.
          this._raisedEvent = false;
        }
      });
    }
  }

  public dispose(): void {
    this._events.dispose();
  }

  @autobind
  public registerKeyboardShortcut(props: IKeyboardShortcutProps): () => void {
    this._shortcuts[props.cmdKey] = {
      keySequences: props.keySequences,
      description: props.description!,
      onExecute: props.onExecute!
    };

    return () => {
      delete this._shortcuts[props.cmdKey];
    };
  }

  @autobind
  public registerActiveChild(isActive: boolean): void {
    if (isActive) {
      this._hasActiveChildren++;
    } else {
      this._hasActiveChildren--;
      if (this._hasActiveChildren === 0) {
        this.setIsActive(true);
      }
    }
  }

  @autobind
  public hasActiveChildren(): boolean {
    return this._hasActiveChildren > 0;
  }

  @autobind
  public setIsActive(active: boolean): void {
    if (active && !this._isActive) {
      this._isActive = active;
      this._raisedEvent = true;
      // notify all other keyboardZone to reset to false
      EventGroup.raise(document, 'resetKeyboardZoneState', this, true);
    }
  }

  @autobind
  private _onProcessKeys(event: KeyboardEvent): void {
    const eventTarget = event.target;
    const keys = getKeyPressed(event);

    // final place to check if the shortcut is valid and allowed for the given target element
    if (eventTarget && (isInputElement(eventTarget) || isButtonElement(eventTarget))) {
      return;
    }
    const onExecute = this._shortcuts[keys] ? this._shortcuts[keys].onExecute : undefined;

    if (onExecute && (this._isActive || this._isDefault) && !this.hasActiveChildren()) {
      onExecute();
      event.preventDefault();
    }
  }
}

const contextDefaultValue: IKeyboardContext = {
  keyboardManager: new KeyboardManager({ isDefault: true /* isDefault */ })
};
export const keyboardContext: IContextComponents<IKeyboardContext> =
  createContext<IKeyboardContext>('keyboard', contextDefaultValue);
